import { Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router'
import { AuthService } from '@auth/services/auth.service'
import { Observable, of } from 'rxjs'
import { catchError, map, switchMap } from 'rxjs/operators'

/**
 * Allows/ denies access to authenticated routes
 */
@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  /**
   * Allows user navigation based on their login state or redirects the user to the login page.
   * Also consumes invite deep-link params (`email` / `workspace_id`) once the user is authenticated.
   * @param next next route
   * @param state current route state
   * @returns boolean
   */
  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (this.authService.isLoggedIn && this.authService.user) {
      return this.authService.handleInviteDeepLink$().pipe(map(() => true))
    }

    const stepUpRequested = this.authService.isStepUpRequested()

    return this.authService.getAccessToken$().pipe(
      switchMap((token) => {
        if (!token) {
          this.authService.beginLogin(state.url, stepUpRequested)
          return of(false)
        }
        return this.authService.getUserData().pipe(
          switchMap(() => this.authService.handleInviteDeepLink$()),
          map(() => true)
        )
      }),
      catchError(() => {
        this.authService.beginLogin(state.url, stepUpRequested)
        return of(false)
      })
    )
  }
  /**
   * Creates the guard and injects it's dependencies
   * @param router Router
   * @param authService AuthService
   */
  constructor(private router: Router, private authService: AuthService) {}
}
